import { useEffect, useState } from "react";
import { WifiOff, Wifi } from "lucide-react";

/**
 * Calm connection pill: shows a gentle floating notice while offline and a
 * brief "back online" confirmation. Never blocks the screen.
 * Also helps on phones: when the keyboard opens, the focused field is
 * scrolled into view so Save / Submit buttons are never hidden.
 */
export function NetworkStatusMonitor() {
  const [online, setOnline] = useState(typeof navigator === "undefined" ? true : navigator.onLine);
  const [showBack, setShowBack] = useState(false);

  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | undefined;
    const up = () => {
      setOnline(true);
      setShowBack(true);
      t = setTimeout(() => setShowBack(false), 2500);
    };
    const down = () => {
      setOnline(false);
      setShowBack(false);
    };
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => {
      window.removeEventListener("online", up);
      window.removeEventListener("offline", down);
      if (t) clearTimeout(t);
    };
  }, []);

  // Keyboard resilience: keep focused fields visible above the on-screen keyboard.
  useEffect(() => {
    const isTouch = window.matchMedia?.("(pointer: coarse)").matches;
    if (!isTouch) return;
    const onFocus = (e: FocusEvent) => {
      const el = e.target as HTMLElement | null;
      if (!el || !el.matches?.("input, textarea, select, [contenteditable='true']")) return;
      setTimeout(() => el.scrollIntoView({ block: "center", behavior: "smooth" }), 300);
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, []);

  if (online && !showBack) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed inset-x-0 z-[100] flex justify-center px-4"
      style={{ top: "calc(0.75rem + env(safe-area-inset-top, 0px))" }}
    >
      <div className="flex items-center gap-2 rounded-full border border-border bg-background/95 px-4 py-2 text-xs font-medium text-foreground shadow-lg backdrop-blur">
        {online ? (
          <>
            <Wifi className="h-4 w-4 text-primary" aria-hidden />
            You're back online
          </>
        ) : (
          <>
            <WifiOff className="h-4 w-4 text-muted-foreground" aria-hidden />
            Offline — your progress is safe. Reconnecting…
          </>
        )}
      </div>
    </div>
  );
}
